import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { toast } from "react-toastify";

import useContract from "../../hooks/useContract";

import {
  Page,
  Panel,
  Stat,
  Progress,
  formatRaw,
  formatInteger,
  tierLabel,
  getError,
  USD_DECIMALS,
} from "./ktlShared";

const GOALS = {
  TOP_10: {
    label: "Reach the Top 10",
    type: "rank",
    target: 10,
  },

  KP_500: {
    label: "Reach 500 KP",
    type: "kp",
    target: 500,
  },

  VOL_10000: {
    label: "Generate $10,000 volume",
    type: "volume",
    target: 10000,
  },

  REWARD_100000: {
    label: "Earn estimated 100,000 KIND",
    type: "reward",
    target: 100000,
  },
};

const Profile = () => {
  const {
    address,
    isConnected,
    getUserDashboardData,
    setUsername,
  } = useContract();

  const [data, setData] = useState(null);

  const [username, setUsernameInput] =
    useState("");

  const [goalKey, setGoalKey] =
    useState(() => {
      return (
        localStorage.getItem(
          "ktl-goal"
        ) || "KP_500"
      );
    });

  const [loading, setLoading] =
    useState(false);

  const [savingUsername, setSavingUsername] =
    useState(false);

  const [error, setError] =
    useState("");

  const load = useCallback(async () => {
    if (!address) {
      setData(null);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const result =
        await getUserDashboardData(
          address
        );

      setData(result);

      setUsernameInput(
        result?.wallet?.username ||
          ""
      );
    } catch (err) {
      console.error(
        "Profile load error:",
        err
      );

      setError(
       
      );
    } finally {
      setLoading(false);
    }
  }, [
    address,
    getUserDashboardData,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const token =
    data?.rewardToken || {
      symbol: "KIND",
      decimals: 18,
    };

  const selectedGoal =
    GOALS[goalKey] ||
    GOALS.KP_500;

  const goalProgress =
    useMemo(() => {
      if (
        !data ||
        !selectedGoal
      ) {
        return {
          current: 0,
          percent: 0,
          remaining: 0,
          completed: false,
          remainingLabel: "0",
        };
      }

      if (
        selectedGoal.type ===
        "rank"
      ) {
        const currentRank =
          Number(
            data.wallet
              ?.currentRank || 0
          );

        const completed =
          currentRank > 0 &&
          currentRank <=
            selectedGoal.target;

        const remaining =
          currentRank >
          selectedGoal.target
            ? currentRank -
              selectedGoal.target
            : 0;

        const percent =
          completed
            ? 100
            : currentRank > 0
            ? Math.min(
                (selectedGoal.target /
                  currentRank) *
                  100,
                99.9
              )
            : 0;

        return {
          current: currentRank,
          percent,
          remaining,
          completed,
          remainingLabel:
            remaining > 0
              ? `${remaining} rank positions`
              : "0",
        };
      }

      let current = 0;

      if (
        selectedGoal.type ===
        "kp"
      ) {
        current = Number(
          data.kpBreakdown
            ?.totalKp || 0
        );
      }

      if (
        selectedGoal.type ===
        "volume"
      ) {
        current = Number(
          formatRaw(
            data.wallet
              ?.currentLeagueVolumeUsd,
            USD_DECIMALS,
            8
          )
        );
      }

      if (
        selectedGoal.type ===
        "reward"
      ) {
        current = Number(
          formatRaw(
            data.estimatedReward
              ?.estimatedKind,
            token.decimals,
            8
          )
        );
      }

      const remaining =
        Math.max(
          selectedGoal.target -
            current,
          0
        );

      const completed =
        current >=
        selectedGoal.target;

      const percent =
        selectedGoal.target > 0
          ? Math.min(
              (current /
                selectedGoal.target) *
                100,
              100
            )
          : 0;

      let remainingLabel =
        remaining.toLocaleString(
          "en-GB",
          {
            maximumFractionDigits: 2,
          }
        );

      if (
        selectedGoal.type ===
        "kp"
      ) {
        remainingLabel += " KP";
      }

      if (
        selectedGoal.type ===
        "volume"
      ) {
        remainingLabel =
          `$${remainingLabel}`;
      }

      if (
        selectedGoal.type ===
        "reward"
      ) {
        remainingLabel +=
          ` ${token.symbol}`;
      }

      return {
        current,
        percent,
        remaining,
        completed,
        remainingLabel,
      };
    }, [
      data,
      selectedGoal,
      token.decimals,
      token.symbol,
    ]);

  const handleUsernameSave =
    async () => {
      const cleanUsername =
        username.trim();

      if (!cleanUsername) {
        toast.error(
          "Enter a username."
        );

        return;
      }

      try {
        setSavingUsername(true);

        await setUsername(
          cleanUsername
        );

        toast.success(
          "Username updated."
        );

        await load();
      } catch (err) {
        console.error(
          "Username update error:",
          err
        );

        toast.error(
          getError(err)
        );
      } finally {
        setSavingUsername(false);
      }
    };

  const saveGoal = (key) => {
    if (!GOALS[key]) {
      return;
    }

    localStorage.setItem(
      "ktl-goal",
      key
    );

    setGoalKey(key);

    toast.success(
      "Personal goal saved."
    );
  };

  if (!isConnected) {
    return (
      <Page
        eyebrow="Wallet profile"
        title="Profile"
        description="Connect your wallet to view your KIND Trading League profile."
      >
        <Panel>
          <p className="text-sm text-[#888]">
            Connect your wallet to view and
            update your profile.
          </p>
        </Panel>
      </Page>
    );
  }

  return (
    <Page
      eyebrow="Wallet profile"
      title="Profile"
      description="Your wallet remains the official identity. Username and personal goals are optional."
      action={
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="rounded-[8px] border border-[#ffcc66]/25 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-[#ffcc66] disabled:opacity-50"
        >
          {loading
            ? "Refreshing"
            : "Refresh"}
        </button>
      }
    >
      {error ? (
        <div className="mb-5 rounded-xl border border-red-400/20 bg-red-400/10 p-4 text-sm text-red-300">
          {error}
        </div>
      ) : null}

      <Panel className="mb-6 bg-gradient-to-br from-[#111314] to-[#29240d]">
        <div className="flex flex-col justify-between gap-5 md:flex-row md:items-center">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] text-[#ffcc66]">
              Public Profile
            </p>

            <h2 className="mt-2 text-3xl font-black">
              {data?.wallet
                ?.username ||
                "Anon"}
            </h2>

            <p className="mt-2 break-all font-mono text-xs text-[#777]">
              {address}
            </p>
          </div>

          <div className="md:text-right">
            <p className="text-sm text-[#888]">
              {tierLabel(
                data?.tierId ||
                  data?.wallet?.tier
              )}
            </p>

            <p className="mt-1 text-3xl font-black text-[#ffcc66]">
              {Number(
                data?.wallet
                  ?.currentRank || 0
              ) > 0
                ? `#${data.wallet.currentRank.toString()}`
                : "Unranked"}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            value={username}
            onChange={(event) =>
              setUsernameInput(
                event.target.value
              )
            }
            placeholder="Public username"
            className="flex-1 rounded-[10px] border border-white/10 bg-black/30 px-4 py-3 text-white outline-none placeholder:text-[#555] focus:border-[#ffcc66]/40"
          />

          <button
            type="button"
            onClick={
              handleUsernameSave
            }
            disabled={
              savingUsername
            }
            className="rounded-[10px] bg-[#ffcc66] px-6 py-3 text-[11px] font-black uppercase tracking-[0.13em] text-[#191a1b] disabled:opacity-50"
          >
            {savingUsername
              ? "Saving"
              : "Save Username"}
          </button>
        </div>
      </Panel>

      <div className="mb-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Current KP"
          value={`${formatInteger(
            data?.kpBreakdown
              ?.currentLeagueKp
          )} KP`}
        />

        <Stat
          label="Pending KP"
          value={`${formatInteger(
            data?.kpBreakdown
              ?.totalPendingKp
          )} KP`}
        />

        <Stat
          label="Total KP"
          value={`${formatInteger(
            data?.kpBreakdown
              ?.totalKp
          )} KP`}
          accent
        />

        <Stat
          label="Current Rank"
          value={
            Number(
              data?.wallet
                ?.currentRank || 0
            ) > 0
              ? `#${data.wallet.currentRank.toString()}`
              : "Unranked"
          }
        />

        <Stat
          label="League Volume"
          value={`$${formatRaw(
            data?.wallet
              ?.currentLeagueVolumeUsd,
            USD_DECIMALS
          )}`}
        />

        <Stat
          label="Lifetime Volume"
          value={`$${formatRaw(
            data?.wallet
              ?.lifetimeVolumeUsd,
            USD_DECIMALS
          )}`}
        />

        <Stat
          label="Highest Rank"
          value={
            Number(
              data?.wallet
                ?.highestRank || 0
            ) > 0
              ? `#${data.wallet.highestRank.toString()}`
              : "—"
          }
        />

        <Stat
          label="Archived KP"
          value={`${formatInteger(
            data?.wallet
              ?.archivedKp
          )} KP`}
        />

        <Stat
          label="Rewards Earned"
          value={`${formatRaw(
            data?.wallet
              ?.totalRewardsEarned,
            token.decimals,
            4
          )} ${token.symbol}`}
        />

        <Stat
          label="Rewards Claimed"
          value={`${formatRaw(
            data?.wallet
              ?.totalRewardsClaimed,
            token.decimals,
            4
          )} ${token.symbol}`}
        />

        <Stat
          label="Estimated Reward"
          value={`${formatRaw(
            data?.estimatedReward
              ?.estimatedKind,
            token.decimals,
            4
          )} ${token.symbol}`}
          accent
        />

        <Stat
          label="Qualification"
          value={
            data?.wallet?.qualified
              ? "Qualified"
              : "Not Qualified"
          }
        />
      </div>

      <Panel>
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] text-[#ffcc66]">
            Personal Goal
          </p>

          <h3 className="mt-2 text-xl font-black">
            Select Your Trading Goal
          </h3>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {Object.entries(
            GOALS
          ).map(
            ([key, item]) => (
              <button
                key={key}
                type="button"
                onClick={() =>
                  saveGoal(key)
                }
                className={`rounded-xl border p-4 text-left text-sm transition ${
                  goalKey === key
                    ? "border-[#ffcc66]/40 bg-[#ffcc66]/10 text-[#ffcc66]"
                    : "border-white/10 bg-black/20 text-[#aaa] hover:border-[#ffcc66]/20"
                }`}
              >
                {item.label}
              </button>
            )
          )}
        </div>

        <div className="mt-6">
          <Progress
            value={
              goalProgress.percent
            }
            max={100}
          />
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <Stat
            label="Selected Goal"
            value={
              selectedGoal.label
            }
          />

          <Stat
            label="Remaining"
            value={
              goalProgress
                .remainingLabel
            }
          />

          <Stat
            label="Status"
            value={
              goalProgress.completed
                ? "Completed"
                : "Incomplete"
            }
            accent={
              goalProgress.completed
            }
          />
        </div>
      </Panel>
    </Page>
  );
};

export default Profile;